import cs from "classnames";

import {
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { ECP_ID_PARAM } from "@js/apps/admin/components/ecp-view-content/constants";
import { openLocationMismatchModal } from "@js/apps/admin/components/location-mismatch-modal";
import { BidRatingCard } from "@js/apps/common/components";
import { FRONTEND_ADMIN_URL } from "@js/apps/common/constants";
import { useIsNodeStaff } from "@js/apps/common/hooks";
import { LocationWarning } from "@js/apps/freelancer/components/location-warning";
import { inviteApi } from "@js/apps/jobs/api";
import { LimitedFreelancerLinks } from "@js/apps/jobs/apps/bids/components";
import { ApproveTalentButton } from "@js/apps/jobs/apps/bids/components/bid-cta-button/components";
import { useCanManageJobsOnBehalfOfClient } from "@js/apps/jobs/hooks";
import { BidSkillChip } from "@js/apps/skills/components/";
import { RouterLink } from "@js/components/link";
import { useAppDispatch } from "@js/hooks";
import type { Job, TalentInviteSuggestion } from "@js/types/jobs";

import { ResumeMatchSummary } from "../../apps/bids/components/resume-match-summary";
import { CannotMakeAction } from "../cannot-make-action";
import { InviteToJobButton } from "../invite-to-job-button";
import { LookingForWorkBadge } from "../looking-for-work-badge";
import { ViewProfileButton } from "../view-profile-button";

import {
  ActionMenu,
  InviteAvatarWithScoreMatch,
  InviteTalentStatusLabel,
} from "./components";

import style from "./style.module.scss";

type JobBidListItemProps = {
  freelancer: TalentInviteSuggestion;
  job: Job;
  notificationHandler: () => void;
};

export const InviteTalentCardItem = ({
  freelancer,
  job,
  notificationHandler,
}: JobBidListItemProps) => {
  const isMobile = useMediaQuery(1280);
  const isNodeStaff = useIsNodeStaff();
  const dispatch = useAppDispatch();
  const canManageJobsOnBehalfOfClient = useCanManageJobsOnBehalfOfClient();
  const matchedSkills = freelancer.matching_skills;
  const availabilityForWork = freelancer.availability_for_work;
  const isLocationMismatched =
    !!freelancer.location_mismatch || !freelancer.location;
  const displayLocationMismatch = isLocationMismatched && isNodeStaff;
  const handleLocationWarningClick = () => {
    openLocationMismatchModal({ userId: freelancer.id });
  };
  const showLookingForWorkBadge = !!availabilityForWork;
  const isFreelancerApproved = freelancer.user.freelancer_approved;
  let profileLink = freelancer.link;
  if (isNodeStaff && freelancer.ecp_id) {
    profileLink = `/${FRONTEND_ADMIN_URL}/ecp?${ECP_ID_PARAM}=${freelancer.ecp_id}`;
  }
  const refetchTalentInviteSuggestions = () => {
    dispatch(inviteApi.util.invalidateTags(["TalentInviteSuggestions"]));
  };
  return (
    <Box
      className={cs(style.inviteTalentCard, {
        [style.inviteTalentCardInvited]: freelancer.invite_status,
      })}
    >
      <Box
        className={style.inviteTalentCardTopSection}
        sx={{ pt: "var(--py)" }}
      >
        <Box
          sx={{
            display: "flex",
            width: { xs: "100%", sm: "auto" },
            minWidth: 0,
            alignItems: "center",
            pl: "var(--px)",
          }}
        >
          <Box sx={{ minWidth: 56, width: 56, height: 56 }}>
            <RouterLink to={freelancer.link} target="_blank">
              <InviteAvatarWithScoreMatch
                freelancer={freelancer}
                jobRoleName={job.role.name}
              />
            </RouterLink>
          </Box>
          <Box className={style.inviteTalentCardName}>
            <Stack sx={{ minWidth: 0 }}>
              <Typography
                ellipsis
                variant="label"
                size="medium"
                component="h3"
                noTextDecoration
                to={freelancer.link}
                target="_blank"
                sx={{ display: "block" }}
                RouterLink={RouterLink}
              >
                {freelancer.user.public_name}
              </Typography>
              <Stack direction="row">
                <Typography variant="paragraph" size="small" component="h4">
                  {freelancer?.role?.name}
                </Typography>
                {(freelancer.location || displayLocationMismatch) && (
                  <Typography
                    variant="paragraph"
                    size="small"
                    component="h4"
                    sx={{ display: "flex", gap: 0.5 }}
                  >
                    {freelancer?.role?.name && (
                      <Box component="span" sx={{ pl: 0.5 }}>
                        -
                      </Box>
                    )}
                    {freelancer.location}
                    {displayLocationMismatch && (
                      <LocationWarning
                        onClick={handleLocationWarningClick}
                        sx={{
                          display: "inline !important",
                          button: { mt: "-3px" },
                        }}
                      />
                    )}
                  </Typography>
                )}
              </Stack>
              {showLookingForWorkBadge && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <LookingForWorkBadge
                    availabilityForWork={availabilityForWork}
                    freelancer={freelancer}
                  />
                </Box>
              )}
            </Stack>
          </Box>
        </Box>

        {isMobile && !!freelancer.external_profiles.length && (
          <Divider color="beige" style={{ margin: "12px 0" }} />
        )}

        <Box
          className={style.inviteTalentCardLinksStatus}
          sx={{
            pr: "var(--px)",
            pl: { xs: "var(--px)", lg: 0 },
          }}
        >
          <InviteTalentStatusLabel status={freelancer.invite_status} />
          <LimitedFreelancerLinks
            externalProfiles={freelancer.external_profiles}
          />
        </Box>
      </Box>

      <Box>
        <Divider color="beige" style={{ marginBottom: "12px" }} />

        {!!freelancer.ada_result && (
          <ResumeMatchSummary
            resumeSummary={freelancer.ada_result}
            jobRoleName={job.role.name}
          />
        )}

        <Box className={style.inviteTalentCardSkills} sx={{ px: "var(--px)" }}>
          {!!matchedSkills.length && (
            <Box sx={{ display: "flex", flexWrap: "wrap", mr: "auto", gap: 1 }}>
              {matchedSkills.map((item) => {
                return <BidSkillChip key={item.id} skill={item.skill} />;
              })}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              flexShrink: { md: 0 },
              gap: 1,
            }}
          >
            <ViewProfileButton profileLink={profileLink} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <ApproveTalentButton
                job={job}
                isTalentApproved={isFreelancerApproved}
                canManageJobsOnBehalfOfClient={canManageJobsOnBehalfOfClient}
                freelancerId={freelancer.id}
                refetchBidList={refetchTalentInviteSuggestions}
              />
              <CannotMakeAction job={job}>
                <Tooltip
                  title="Talent must be approved to be invited"
                  disabled={isFreelancerApproved}
                >
                  <span>
                    <InviteToJobButton
                      freelancer={freelancer}
                      jobId={job.id}
                      disabled={!job.is_open || !isFreelancerApproved}
                      notificationHandler={notificationHandler}
                    />
                  </span>
                </Tooltip>
              </CannotMakeAction>
              <ActionMenu jobId={job.id} freelancer={freelancer} />
            </Box>
          </Box>
        </Box>

        <BidRatingCard freelancer={freelancer} />
      </Box>
    </Box>
  );
};
